import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";
import type {
  CreateSequenceInput,
  LaunchResult,
  Paginated,
  PreviewResult,
  Sequence,
  SequenceSummary,
  Template,
} from "./types";

const campaignKey = (id: string) => ["campaign", getActiveTenantId(), id] as const;

/**
 * POST /api/campaigns/{id}/copy — generate a copy template. Both fields optional:
 * `prompt` is a per-campaign/per-step brief; `stepIndex` (ADR-0025) selects the tenant's
 * per-step follow-up guidance so each step of a drip can have its own tone.
 */
export function useGenerateCopy(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { prompt?: string; stepIndex?: number }) =>
      httpClient<Template>(`/api/campaigns/${campaignId}/copy`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: campaignKey(campaignId) }),
  });
}

/** GET /api/sequences?campaignId= — the sequences that belong to THIS campaign (ADR-0025). */
export function useCampaignSequences(campaignId: string) {
  return useQuery({
    queryKey: ["sequences", "campaign", getActiveTenantId(), campaignId] as const,
    queryFn: () =>
      httpClient<Paginated<SequenceSummary>>(`/api/sequences?campaignId=${encodeURIComponent(campaignId)}`),
    enabled: !!campaignId,
  });
}

/**
 * GET /api/campaigns/{id}/preview — render the latest template over the frozen
 * audience. Modelled as an on-demand action (mutation) since the user triggers
 * it; it reads, never writes.
 */
export function usePreview(campaignId: string) {
  return useMutation({
    mutationFn: () => httpClient<PreviewResult>(`/api/campaigns/${campaignId}/preview`),
  });
}

/** POST /api/campaigns/{id}/sequence — create a multi-step sequence. */
export function useCreateSequence(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSequenceInput) =>
      httpClient<Sequence>(`/api/campaigns/${campaignId}/sequence`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: campaignKey(campaignId) });
      qc.invalidateQueries({ queryKey: ["sequences"] }); // refresh the campaign's sequence list (ADR-0025)
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
}

/** POST /api/sequences/{id}/launch — push to the provider and enroll the audience. */
export function useLaunchSequence(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sequenceId: string) =>
      httpClient<LaunchResult>(`/api/sequences/${sequenceId}/launch`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: campaignKey(campaignId) });
      qc.invalidateQueries({ queryKey: ["sequences"] }); // refresh the campaign's sequence list (ADR-0025)
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
}
